import { useState, useEffect, useMemo } from 'react';

import { useParams, useNavigate } from 'react-router-dom';

import { BsBuilding, BsEnvelope, BsPersonPlus } from 'react-icons/bs';

import AddToContactListModal from '#contactListFeature/components/AddToContactListModal/AddToContactListModal';
import TransferLeadsToCrmModal from '#crmFeature/components/leads/TransferLeadsToCrmModal/TransferLeadsToCrmModal';
import Button from '#globalComponents/Button/Button';
import EmptyState from '#globalComponents/EmptyState/EmptyState';
import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';
import { useCapabilityCheck } from '#globalHooks/useCapabilityCheck';
import { usePreferencesStore } from '#lib/stores/preferencesStore';

import CompanyBusinessPurpose from '../../components/CompanyDetailPage/CompanyBusinessPurpose';
import CompanyContacts from '../../components/CompanyDetailPage/CompanyContacts';
import CompanyDetailHeader from '../../components/CompanyDetailPage/CompanyDetailHeader';
import CompanyDetailNavigation from '../../components/CompanyDetailPage/CompanyDetailNavigation';
import CompanyDirectors from '../../components/CompanyDetailPage/CompanyDirectors';
import CompanyEventsTimeline from '../../components/CompanyDetailPage/CompanyEventsTimeline';
import CompanyFinancialChart from '../../components/CompanyDetailPage/CompanyFinancialChart';
import CompanyFinancialKPIs from '../../components/CompanyDetailPage/CompanyFinancialKPIs';
import CompanyFinancialTable from '../../components/CompanyDetailPage/CompanyFinancialTable';
import CompanyMasterData from '../../components/CompanyDetailPage/CompanyMasterData';
import {
	useCompanyDetail,
	useCompanyFinancialHistory,
	useCompanyEvents,
	useCompanyContacts,
	useCompanyDirectors,
} from '../../models/companies/queries';
import { useRecentCompaniesStore } from '../../stores/recentCompaniesStore';

const LEADS_PATH = '/leads';

const buildSections = ({ hasFinancials, hasDirectors, hasEvents }) =>
	[
		{ id: 'uebersicht', label: 'Übersicht', show: true },
		{ id: 'kennzahlen', label: 'Kennzahlen', show: hasFinancials },
		{ id: 'bilanz', label: 'Bilanz & GuV', show: hasFinancials },
		{ id: 'geschaeftsfuehrung', label: 'Geschäftsführung', show: hasDirectors },
		{ id: 'historie', label: 'Historie', show: hasEvents },
	].filter((s) => s.show);

const CompanyDetailPage = () => {
	const { id } = useParams();
	const navigate = useNavigate();

	const { data: company, isLoading: isLoadingCompany } = useCompanyDetail(id);
	const globalCompanyId = company?.globalCompanyId;
	const setLastSelectedSearchQueryId = usePreferencesStore((s) => s.setLastSelectedSearchQueryId);
	const addRecentCompany = useRecentCompaniesStore((s) => s.addRecentCompany);

	const { hasCapability: hasCrmAccess } = useCapabilityCheck('crm.access');
	const [selectedContactIds, setSelectedContactIds] = useState([]);
	const [showContactListModal, setShowContactListModal] = useState(false);
	const [showCrmModal, setShowCrmModal] = useState(false);

	// Store the prospectQueryId so /leads shows the correct query when navigating back
	useEffect(() => {
		if (company?.prospectQueryId) {
			setLastSelectedSearchQueryId(company.prospectQueryId);
		}
	}, [company?.prospectQueryId, setLastSelectedSearchQueryId]);

	useEffect(() => {
		if (company?.companyName) {
			addRecentCompany({ id, companyName: company.companyName, city: company.addressCity ?? null });
		}
	}, [id, company?.companyName, company?.addressCity, addRecentCompany]);

	const { data: financials = [] } = useCompanyFinancialHistory(globalCompanyId);
	const { data: events = [] } = useCompanyEvents(globalCompanyId);
	const { data: contacts = [] } = useCompanyContacts(id);
	const { data: directors = [] } = useCompanyDirectors(globalCompanyId);

	const hasFinancials = financials.length > 0;
	const hasDirectors = directors.length > 0;
	const hasEvents = events.length > 0;
	const sections = useMemo(
		() => buildSections({ hasFinancials, hasDirectors, hasEvents }),
		[hasFinancials, hasDirectors, hasEvents],
	);

	if (isLoadingCompany) {
		return (
			<Page scrollMode="content">
				<div className="flex flex-1 items-center justify-center">
					<div className="h-7 w-7 animate-spin rounded-full border-b-2 border-primary-500" />
				</div>
			</Page>
		);
	}

	if (!company) {
		return (
			<Page scrollMode="content">
				<EmptyState
					className="flex-1 justify-center"
					icon={BsBuilding}
					title="Unternehmen nicht gefunden"
					action={<Button onClick={() => navigate(LEADS_PATH)}>Zurück zur Übersicht</Button>}
				/>
			</Page>
		);
	}

	const canTransferToCrm = hasCrmAccess && Boolean(company.prospectQueryId);
	const headerActions = (
		<>
			<Button
				variant="outline"
				size="sm"
				disabled={selectedContactIds.length === 0}
				onClick={() => setShowContactListModal(true)}
			>
				<BsEnvelope className="h-4 w-4" />
				Zur Kontaktliste
			</Button>
			{canTransferToCrm && (
				<Button size="sm" onClick={() => setShowCrmModal(true)}>
					<BsPersonPlus className="h-4 w-4" />
					Ins CRM übernehmen
				</Button>
			)}
		</>
	);

	return (
		<Page
			header={
				<PageHeader
					breadcrumbs={[{ label: 'Lead-Übersicht', to: LEADS_PATH }, company.companyName ?? 'Unternehmen']}
				/>
			}
		>
			<CompanyDetailHeader company={company} actions={headerActions} />
			<CompanyDetailNavigation sections={sections} />

			<div className="space-y-4 pb-12">
				<div id="uebersicht" className="grid items-start gap-4 lg:grid-cols-2">
					<CompanyContacts
						contacts={contacts}
						companyId={id}
						prospectQueryId={company.prospectQueryId}
						selectedContactIds={selectedContactIds}
						setSelectedContactIds={setSelectedContactIds}
					/>
					<div className="space-y-4">
						<CompanyMasterData company={company} />
						{company.businessPurpose && (
							<CompanyBusinessPurpose businessPurpose={company.businessPurpose} />
						)}
					</div>
				</div>

				{hasFinancials && (
					<div id="kennzahlen" className="space-y-4">
						<CompanyFinancialKPIs financials={financials} />
						{financials.length > 1 && <CompanyFinancialChart financials={financials} />}
					</div>
				)}

				{hasFinancials && (
					<div id="bilanz">
						<CompanyFinancialTable financials={financials} />
					</div>
				)}

				{hasDirectors && (
					<div id="geschaeftsfuehrung">
						<CompanyDirectors directors={directors} />
					</div>
				)}

				{hasEvents && (
					<div id="historie">
						<CompanyEventsTimeline events={events} />
					</div>
				)}
			</div>

			<AddToContactListModal
				isOpen={showContactListModal}
				onClose={() => setShowContactListModal(false)}
				globalContactPersonIds={selectedContactIds}
				onSuccess={() => setSelectedContactIds([])}
			/>

			{canTransferToCrm && (
				<TransferLeadsToCrmModal
					isOpen={showCrmModal}
					onClose={() => setShowCrmModal(false)}
					tableScope={{ table: 'leads', contextId: company.prospectQueryId, ids: [id] }}
					count={1}
				/>
			)}
		</Page>
	);
};

export default CompanyDetailPage;
